import { useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import { AutoComplete, Button, Card, Typography, Tag, Tabs, Empty, Input } from 'antd'
import { SearchOutlined, StarOutlined, StarFilled} from '@ant-design/icons'
import { useFeedback } from '@/utils/antdApp'
import AddStockDialog from '../components/add-stock-dialog'
import { colors } from '@/pages/static'
import { addStockToPool, addStocksToPoolBatch, updatePool, getStocks } from '@/api'
import './index.scss'
import type { StockPool, Stock } from '@/types'
import { localToUTC } from '@/utils/filters'

type SearchOption = {
    value: string
    label: React.ReactNode
    stock: Stock
}

const { Text } = Typography

const items = [
    { label: '全部股票池', key: '1' },
    { label: '我的关注', key: '2' },
]

export default function PoolListPage({
    poolList,
    refresh,
    toDetail,
    addPool,
    toStock,
}: {
    poolList: StockPool[]
    refresh: () => void
    toDetail: (poolId: number, idx: number) => void
    addPool: () => void
    toStock: (stock: Stock) => void
}) {
    const { message } = useFeedback()
    const [visible, setVisible] = useState(false)
    const [poolId, setPoolId] = useState(0)
    const [stockPool, setStockPool] = useState<StockPool[]>([])
    const [activeKey, setActiveKey] = useState('1')

    const [defaultStocks, setDefaultStocks] = useState<Stock[]>([])
    const [searchOptions, setSearchOptions] = useState<SearchOption[]>([])

    const handleSearch = (value: string) => {
        if (!value.trim()) {
            setSearchOptions([])
            return
        }
        getStocks(value).then((res: Stock[]) => {
            const list = Array.isArray(res) ? res : []
            setSearchOptions(list.map((s) => {
                const name = s.market === 'CN'
                    ? (s.sc_name || s.name)
                    : s.market === 'HK'
                        ? (s.tc_name || s.name)
                        : s.name
                return {
                    value: s.code,
                    label: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ color: '#fff' }}>{name}</span>
                            <span style={{ color: 'var(--text-3)', fontSize: 11 }}>{s.code}</span>
                            {s.market && <span style={{ color: 'var(--text-3)', fontSize: 11 }}>{s.market}</span>}
                        </div>
                    ),
                    stock: { ...s, name: name || s.name },
                }
            }))
        }).catch(() => setSearchOptions([]))
    }

    const handleSelectStock = (_value: string, option: SearchOption) => {
        toStock(option.stock)
    }

    useEffect(() => {
        setStockPool(poolList)
    }, [poolList])

    const onChange = (key: string) => {
        setActiveKey(key)
        if(key === '2') {
            setStockPool(poolList.filter(m => m.is_favorite))
        } else {
            setStockPool(poolList)
        }
    }

    const onFavorite = (e: React.MouseEvent<HTMLSpanElement>, pool: StockPool) => {
        e.stopPropagation()
        updatePool({pool_id: pool.id, is_favorite: !pool.is_favorite}).then(() => {
            message.success(pool.is_favorite ? '取消收藏成功' : '收藏成功')
            refresh()
        }).catch(() => message.error(pool.is_favorite ? '取消收藏失败' : '收藏失败'))
    }

    const onDetail = (data: StockPool, index: number) => {
        toDetail(data.id, index)
    }

    const onAdd = (data: Stock) => {
        addStockToPool({ pool_id: poolId, ...data }).then(() => {
            message.success('添加成功')
            setVisible(false)
            refresh()
        }).catch(() => message.error('添加失败'))
    }

    const onBatchAdd = async (stocks: { code: string; name: string }[]) => {
        try {
            const res = await addStocksToPoolBatch(poolId, stocks)
            const added = res.added?.length ?? 0
            const skipped = res.skipped?.length ?? 0
            message.success(`成功添加 ${added} 只${skipped ? `，跳过 ${skipped} 只` : ''}`)
            setVisible(false)
            refresh()
        } catch (err) {
            message.error(err instanceof Error ? err.message : '批量添加失败')
        }
    }

    return (
        <div className='pool-page'>
            <div className='page-header flex flex-between pool-page-header'>
                <Tabs defaultActiveKey={activeKey} items={items} onChange={onChange} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <AutoComplete
                        options={searchOptions}
                        onSearch={handleSearch}
                        onSelect={handleSelectStock}
                        style={{ width: 220 }}
                        popupMatchSelectWidth={300}
                    >
                        <Input
                            prefix={<SearchOutlined style={{ color: 'var(--text-3)' }} />}
                            placeholder="搜索个股..."
                            size="middle"
                            styles={{ input: { color: '#fff', fontSize: 12 } }}
                        />
                    </AutoComplete>
                    <Button type="primary" onClick={() => addPool()}>
                    + 新建股票池
                    </Button>
                </div>
            </div>
            <div className='page-content'>
                {stockPool.length > 0 ? 
                    <div className='pool-page-content-list'>
                        {stockPool.map((m, index) => (
                            <Card
                                key={m.id}
                                className="pool-page-card"
                                style={{ '--manager-color': colors[index % colors.length], height: '100%' } as CSSProperties}
                                size="small"
                                onClick={() => onDetail(m, index)}
                            >
                                <div className='pool-page-title'>
                                    <div className='pool-page-title-top'>
                                        <div className='flex flex-between'>
                                            <Text style={{ fontSize: 14, fontWeight: 700 }}>{m.name ?? '--'}</Text>
                                            <div className='pool-page-title-tags'>
                                                <Tag variant='outlined' className='pool-page-circle'>
                                                    <a href='#' className='color-blue'>{m.count} 只</a>
                                                </Tag>
                                                <span 
                                                style={{marginLeft: 12}}
                                                onClick={(e) => onFavorite(e,m)}>
                                                    {m.is_favorite ? <StarFilled className='warning'/>: <StarOutlined />}
                                                </span>
                                            </div>
                                        </div>
                                        <span className='pool-page-title-tabs'>{m.description ?? '--'}</span>
                                    </div>
                                </div>
                                <div className='pool-page-content'>
                                    {(Array.isArray(m.stocks) ? m.stocks : []).map((stock,index) => {
                                        const name = stock.market === 'CN'
                                            ? (stock.sc_name || stock.name)
                                            : stock.market === 'HK'
                                                ? (stock.tc_name || stock.name)
                                                : stock.name
                                        return (
                                            <Tag key={index} variant='outlined' className='pool-page-stock-tags'>
                                                <span className='color-2' style={{ marginRight: 2, fontSize: 10 }}>{name}</span> {stock.code ?? '--'}
                                            </Tag>
                                        )
                                    })}
                                    <div className='pool-page-content-footer flex flex-between'>
                                        更新 {localToUTC(m.updatedAt)}
                                        <div className='pool-page-content-footer-button'>
                                            <Button size='small' style={{ marginRight: 10 ,color: 'var(--text-2)'}} onClick={(e) => {
                                                e.stopPropagation()
                                                onDetail(m, index)
                                            }}>
                                                进入管理
                                            </Button>
                                            <Button size='small' style={{color: 'var(--text-2)'}} onClick={(e) => {
                                                e.stopPropagation()
                                                setPoolId(m.id)
                                                setVisible(true)
                                                setDefaultStocks(m.stocks)
                                            }} >
                                                + 添加
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                : (
                    <div className='pool-page-content-list-empty'>
                        <Empty description="暂无股票池" />
                    </div>
                )}
            </div>
            <AddStockDialog 
                open={visible} 
                onCancel={() => setVisible(false)} 
                poolId={poolId} 
                onAdd={onAdd}
                onBatchAdd={onBatchAdd}
                items={defaultStocks} 
            />
        </div>
    )
}
